import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  ScaleIcon,
  LockClosedIcon,
  CpuChipIcon,
  ArrowPathIcon,
  ChartBarIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: EyeIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure checked against federal regulatory rules spanning Reg Z, UDAAP, TILA, FTC Act, and more. AI-powered detection with deterministic rules engine verification and mandatory human approval workflows. Examiner-ready audit trail on every check.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder',
    description:
      'Validate, clean, and prepare LAR data for submission. 160+ edit checks with plain-language AI explanations. ML-assisted column matching for LOS imports (Encompass, Jack Henry, LaserPro, Calyx, MeridianLink). Data quality scoring, geocoding, and submission-ready exports.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Denial rate and pricing disparity analysis with statistical testing. Redlining analysis with census tract mapping and demographic overlays. Marketing content screening across four risk categories: geographic targeting, prohibited basis language, steering indicators, and discouragement patterns. Risk assessment scoring, exception tracking, and continuous monitoring with paired bias testing.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA examination standards. Auto-triggers on upload with quarterly scheduling.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of regulatory updates across 8 federal agencies — CFPB, FDIC, OCC, NCUA, FRB, FHFA, SEC, and FinCEN. Daily scanning of RSS feeds with keyword classification, compliance area tagging, and priority assignment. Alerts when new guidance affects your compliance program.',
  },
];

const fsAiRmfControls = [
  {
    icon: CheckCircleIcon,
    title: 'Human-in-the-Loop by Design',
    description:
      "Clarid's three-layer architecture enforces separation: AI performs detection, a deterministic rules engine performs compliance determination, and humans make the final decision. Every AI-generated finding requires review and approval through role-based workflows. No autonomous compliance decisions.",
    refs: 'FS AI RMF: MG-1.2, MP-1.2, MP-4.4',
  },
  {
    icon: LockClosedIcon,
    title: 'Hash-Chained Audit Trail',
    description:
      'Every interaction — every check, every finding, every approval, every override — is recorded in a SHA-256 hash-chained, append-only audit log. Each entry cryptographically links to its predecessor. Tampering is mathematically detectable through chain integrity verification. 60+ event types, timestamped with user attribution and model identification. Examiner-exportable.',
    refs: 'FS AI RMF: MG-4.4',
  },
  {
    icon: ChartBarIcon,
    title: 'Model Validation & Bias Testing',
    description:
      "Clarid runs formal model validation against 60 curated test fixtures across three AI use cases, computing precision, recall, F1, and accuracy per use case. Separate bias testing uses 28 paired content fixtures across 8 protected-class dimensions (geographic-income, age, gender, race-ethnicity, national origin, religion, disability, and familial status) with Fisher's exact test for statistical significance. Flagging threshold: p < 0.05 and rate difference > 10%, per NIST AI 100-1 guidance.",
    refs: 'FS AI RMF: MS-2.1 through MS-2.5, MS-4.1, MS-4.3, MS-4.4',
  },
  {
    icon: CpuChipIcon,
    title: 'AI Performance Monitoring',
    description:
      'Bank compliance officers see a real-time AI Performance dashboard showing accuracy rate, false positive rate, median review turnaround, provider health status, and audit chain integrity. Administrators see system-level metrics: P50/P95/P99 latency by provider, error rates, call volumes, circuit breaker state, and fallback frequency. Every AI call is instrumented with provider, model, latency, and outcome telemetry.',
    refs: 'FS AI RMF: MS-3.1, MS-3.4, GV-5.2',
  },
  {
    icon: ArrowPathIcon,
    title: 'Multi-Provider Resilience',
    description:
      'Clarid operates across three foundation model providers — OpenAI, Anthropic, and Google — with a circuit breaker pattern and automatic failover. No single-vendor dependency. If one provider experiences degradation, the system routes to alternatives without interruption. Model selection is rule-based and auditable.',
    refs: 'FS AI RMF: MS-2.6',
  },
];

const partnershipModels = [
  {
    num: '01',
    title: 'Consulting Enhancement',
    description:
      "Crowe consultants deploy Clarid as part of compliance program engagements. The technology layer stays with the bank after the engagement, providing continuous monitoring that maintains the standards Crowe helped establish. Crowe provides the strategic expertise. Clarid provides the ongoing execution.",
  },
  {
    num: '02',
    title: 'Referral & Co-Sell',
    description:
      "Crowe recommends Clarid to banking clients who need continuous compliance monitoring. Clarid handles the technology relationship. Crowe maintains the advisory relationship. Both deliver more value to the bank — and the bank sees Crowe as the advisor who connected them with best-in-class technology.",
  },
  {
    num: '03',
    title: 'Alloy Labs Integration',
    description:
      "Introduce Clarid to the Alloy Labs Alliance member banks as a compliance technology solution vetted by Crowe. The Alliance's 80 community banks are specifically organized around fintech adoption — Clarid is purpose-built for exactly this audience.",
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives mapped, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: "Explore alignment between Crowe's fintech advisory practice and Clarid's compliance platform" },
  { num: 2, title: 'Technical & Governance Review', description: "Share FS AI RMF gap analysis, AI governance policy, model inventory, and data flow architecture for Crowe's evaluation" },
  { num: 3, title: 'Joint Client Pilot', description: 'Identify 2–3 Crowe banking clients for a structured pilot with defined success criteria' },
  { num: 4, title: 'Partnership Formalization', description: 'Define the partnership model, referral structure, and go-to-market approach' },
];

/* ─── Component ─── */
export default function CrowePartnerPage() {
  useEffect(() => {
    document.title = 'Crowe + Clarid — AI-Native Compliance for Community Banks';

    const metaTags: Record<string, string> = {
      'og:title': 'Crowe + Clarid — Strategic Partnership',
      'og:description': 'AI-native compliance platform with 100% FS AI RMF coverage. Built by a community bank CMO, advised by former FDIC and Federal Reserve examiners.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Crowe + Clarid Partnership',
      'twitter:description': 'The compliance platform examiners helped build.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/crowe-logo.png" alt="Crowe" className="h-10" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">
              Strategic Partnership
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              The Compliance Platform Examiners Helped Build
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance operating system for community banks — built by a
              community bank CMO, advised by a former FDIC Supervisory Examiner and a former Federal
              Reserve fair lending subject matter expert, and designed to automate the compliance
              workflows banks manage every day.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Problem ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                AI Is Creating New Compliance Surface Area
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks are adopting AI faster than compliance frameworks can keep up.
                  Microsoft Copilot is generating marketing content. AI-assisted tools are touching
                  lending workflows, customer communications, and document creation. Every new capability
                  creates new compliance exposure — and most institutions don't have a systematic way to manage it.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  At the same time, regulatory expectations around HMDA data quality, fair lending monitoring,
                  and marketing compliance continue to increase. Examiners expect continuous processes, not
                  point-in-time reviews. For banks in the $1B–$5B range with 1–3 compliance staff, the math
                  doesn't work.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Banks need technology that applies compliance intelligence in real time — inside the tools
                  their teams already use — with audit trails that meet examiner expectations.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  That's what Clarid was built to do.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              AI-Native Compliance Tools for the Banks You Serve
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools bankers already use — Microsoft Copilot, Claude, ChatGPT — through
              MCP (Model Context Protocol). No new portal to log into. Compliance intelligence embedded in daily workflows.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: FS AI RMF ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">
                AI Governance & The FS AI RMF
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                100% Coverage of the Treasury's AI Risk Management Framework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                The U.S. Treasury released the Financial Services AI Risk Management Framework on
                February 19, 2026 — the first sector-specific implementation of NIST AI RMF for
                financial services, with 230 control objectives across four functions: GOVERN, MAP,
                MEASURE, and MANAGE. Clarid addresses every applicable control subcategory — zero gaps.
                Within 14 days of the framework's release, our architecture was validated against all
                four functions. This isn't a retrofit — Clarid was built with examiner defensibility
                as a first principle, and the framework now formally validates what we already do.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-6 lg:space-y-8 mt-14">
            {fsAiRmfControls.map((control, i) => (
              <ScaleIn key={control.title} delay={i * 0.08}>
                <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-5">
                    <control.icon className="h-7 w-7 text-teal-400 flex-shrink-0" />
                    <h3 className="text-2xl font-display font-bold text-white">{control.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed mb-4">{control.description}</p>
                  <p className="text-sm font-mono text-slate-500">{control.refs}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.5}>
            <p className="mt-14 text-center text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
              Clarid's AI governance framework is fully documented: AI Governance Policy, Model Inventory
              with model cards per provider, Prompt Registry, Incident Response Playbook, and ongoing
              validation processes. Full gap analysis against all 230 FS AI RMF control objectives is
              available for review.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 5: Examination Perspective ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Advised by Former FDIC and Federal Reserve Examiners
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert — with decades of combined
                  examination experience across hundreds of community bank examinations — advise
                  Clarid's development. Every rule engine, every workflow, every output is validated
                  against what examiners actually evaluate during safety and soundness and compliance
                  examinations.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community bank CMO who manages marketing compliance, HMDA
                  reporting, and examiner relationships as part of his daily work. This platform
                  wasn't designed by engineers guessing at compliance needs — it was built by someone
                  who lives them and who will sit across from examiners using the outputs Clarid
                  generates.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 6: Partnership Models ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                How This Works Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Crowe's compliance consulting expertise combined with Clarid's AI-native compliance
                platform gives banks both strategic guidance and real-time technology — the advisory
                framework and the automated execution layer working together.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {partnershipModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>

            <FadeUp delay={0.4}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We'd welcome a conversation about which model — or combination — aligns best with
                Crowe's client strategy and partner framework.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Due Diligence ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet the
                expectations outlined in interagency guidance on third-party relationships. Key
                documentation includes:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Our Executive Due Diligence Summary, Data Flow Architecture, and full FS AI RMF gap
                analysis are available for review. SOC 2 Type I is on our 12-month roadmap — we're
                documenting controls now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Moving Forward ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/crowe-logo.png" alt="Crowe" className="h-7 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
